import { createContext, type Dispatch, type SetStateAction } from 'react';
import type { Asset } from 'src/types/api.types';

/**
 * Represents an asset that has been added to a playlist during creation
 * Uses the Asset type from api.types with additional fields that may be present
 */
interface PlaylistAsset extends Asset {
  /** Thumbnail URL for the asset */
  assetThumbnailFormat?: string;
  /** Display title for the asset */
  akomiTitle?: string;
  /** Clip ID used for referencing the asset */
  akomiClipId?: string;
  /** Media type (mimetype) for icon display */
  mediatype?: string;
}

/**
 * Context type for CreatePlayListContext
 * Manages state for assets being added to a new playlist
 */
interface CreatePlayListContextType {
  /**
   * Array of assets that have been added to the playlist being created
   */
  createAddedAssets: PlaylistAsset[];

  /**
   * State setter for updating the added assets array
   */
  setCreateAddedAssets: Dispatch<SetStateAction<PlaylistAsset[]>>;

  /**
   * ID of the current playlist being created/edited
   * -1 indicates no playlist is currently selected
   */
  currentPlayList: number;

  /**
   * State setter for updating the current playlist ID
   */
  setCurrentPlayList: Dispatch<SetStateAction<number | undefined>>;
}

const defaultValue: CreatePlayListContextType = {
  createAddedAssets: [],
  setCreateAddedAssets: () => {
    // Default no-op function - will be overridden by provider
  },
  currentPlayList: -1,
  setCurrentPlayList: () => {
    // Default no-op function - will be overridden by provider
  },
};

const CreatePlayListContext = createContext<CreatePlayListContextType>(defaultValue);

export default CreatePlayListContext;
export type { CreatePlayListContextType, PlaylistAsset };
